import { useState, useEffect } from "react";
import { Globe, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, useParams } from "react-router-dom";
import { AITeamLoading } from "./AITeamLoading";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { validateURL } from "@/lib/input-validation";

// Basic interface for analysis results to improve type safety
interface AnalysisResults {
  description?: string;
  brandDescription?: string;
  features?: Array<{ name: string; items: string[] }>;
  targetAudience?: Array<{ segment: string; description: string }>;
  painPoints?: Array<{ problem: string }>;
  desires?: Array<{ desire: string }>;
  brandPositioning?: Array<{ aspect: string; description: string }>;
  keywords?: {
    core?: string[];
    competitive?: string[];
    niche?: string[];
    longTail?: string[];
  };
  marketAnalysis?: {
    positioning?: any;
    competitiveLandscape?: any;
    targetMarket?: any;
  };
  websiteUrl?: string;
  [key: string]: any; // Allow other properties
}

export const AnalyzeStep = ({ 
  onAnalysisComplete, 
  onAnalysisStart,
  projectId: propProjectId,
  initialUrl = "",
  isOnboarding = false
}: { 
  onAnalysisComplete: (results: AnalysisResults) => void;
  onAnalysisStart?: (url?: string) => void;
  projectId?: string | null;
  initialUrl?: string;
  isOnboarding?: boolean;
}) => {
  const [url, setUrl] = useState(initialUrl);
  const [urlError, setUrlError] = useState<string | undefined>(undefined);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTask, setCurrentTask] = useState("");
  const [hasExistingAnalysis, setHasExistingAnalysis] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { id: paramProjectId } = useParams();
  
  // Use provided project ID from props if available, otherwise use the one from URL params
  const projectId = propProjectId || paramProjectId;

  // Auto-trigger analysis when component mounts with an initialUrl
  useEffect(() => {
    if (initialUrl && !isAnalyzing) {
      try {
        // Validate the initial URL before proceeding
        const validation = validateURL(initialUrl);
        if (validation.valid) {
          // Only auto-trigger if URL is valid
          handleAnalyze();
        } else {
          // Show error for invalid initial URL
          setUrlError(validation.message);
          toast({
            title: "Invalid URL",
            description: validation.message || "Please enter a valid website URL",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Error validating URL:", error);
        toast({
          title: "URL Validation Error",
          description: "There was a problem validating the URL. Please try again.",
          variant: "destructive",
        });
      }
    }
  }, [initialUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const checkExistingAnalysis = async () => {
      if (!projectId) return;
      
      const { data: existingCrawl } = await supabase
        .from('website_crawls')
        .select('id')
        .eq('project_id', projectId)
        .eq('status', 'completed')
        .maybeSingle();
      
      setHasExistingAnalysis(!!existingCrawl);
    };

    checkExistingAnalysis();
  }, [projectId]);

  // Validate URL when it changes
  useEffect(() => {
    if (url.trim() === '') {
      setUrlError(undefined);
      return;
    }
    
    // Only validate after user stops typing for 500ms
    const timer = setTimeout(() => {
      const result = validateURL(url);
      setUrlError(result.valid ? undefined : result.message);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [url]);

  const handleAnalyze = async () => {
    // Prevent multiple simultaneous analyses
    if (isAnalyzing) {
      return;
    }
    
    // Validate the URL
    const validation = validateURL(url);
    
    if (!validation.valid || !validation.sanitized) {
      setUrlError(validation.message);
      toast({
        title: "Invalid URL",
        description: validation.message || "Please enter a valid website URL (e.g., https://example.com)",
        variant: "destructive",
      });
      return;
    }

    // Use the sanitized URL
    const validatedUrl = validation.sanitized;

    if (hasExistingAnalysis) {
      const confirmed = window.confirm(
        "This project already has an analysis. Running a new analysis will overwrite the existing results. Are you sure you want to continue?"
      );
      if (!confirmed) return;
    }

    setIsAnalyzing(true);
    if (onAnalysisStart) {
      onAnalysisStart(validatedUrl);
    }
    
    setProgress(0);
    setCurrentTask("Initializing AI team for website analysis...");
    
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        const increment = Math.floor(Math.random() * 15) + 5;
        const newProgress = Math.min(prev + increment, 90);
        
        // Update task messages based on progress
        if (newProgress < 20) {
          setCurrentTask("Crawling website structure and indexing all pages...");
        } else if (newProgress < 40) {
          setCurrentTask("Analyzing meta tags, headers, and content relevance...");
        } else if (newProgress < 60) {
          setCurrentTask("Identifying target audience segments and user personas...");
        } else if (newProgress < 80) {
          setCurrentTask("Benchmarking against industry competitors...");
        } else {
          setCurrentTask("Evaluating technical performance and site architecture...");
        }
        
        return newProgress;
      });
    }, 2000);

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        throw new Error("No authenticated user found");
      }

      // Regular flow - always use the real API
      const { data, error } = await supabase.functions.invoke('crawl-and-analyze', {
        body: { 
          url: validatedUrl,
          projectId,
          userId: user.id
        }
      });

      if (error) {
        throw new Error(error.message);
      }

      clearInterval(progressInterval);
      setProgress(100);
      setCurrentTask("Analysis complete! Preparing to show results...");
      
      if (data.success) {
        if (onAnalysisComplete && data.analysisResults) {
          onAnalysisComplete(data.analysisResults);
          
          // Only navigate to research page if we're NOT in the onboarding flow
          if (!isOnboarding && projectId) {
            navigate(`/projects/${projectId}/research`);
          }
        }

        toast({
          title: "Analysis Complete",
          description: "Successfully analyzed website content",
        });
      }
    } catch (error) {
      clearInterval(progressInterval);
      console.error("Analysis error:", error);
      setProgress(0); // Reset progress
      setCurrentTask(""); // Clear the task message
      
      // ✅ CRITICAL FIX: Always notify parent that analysis completed (even with error)
      if (onAnalysisComplete) {
        onAnalysisComplete(null as any); // Pass null to indicate failure
      }
      
      toast({
        title: "Analysis Failed",
        description: error instanceof Error ? error.message : "Failed to analyze the website. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="space-y-6">
      {!isAnalyzing ? (
        <div className="flex flex-col items-center">
          <div className="w-full max-w-3xl">
            {hasExistingAnalysis && (
              <Alert variant="warning" className="mb-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  This project already has an analysis. Running a new analysis will overwrite the existing results.
                  We recommend reviewing the current analysis before proceeding.
                </AlertDescription>
              </Alert>
            )}
            {/* Don't show the input if in onboarding mode with initialUrl */}
            {(!isOnboarding || !initialUrl) && (
              <div className="flex flex-col md:flex-row gap-4">
                <Input
                  placeholder="Enter website URL"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="flex-1 bg-background/50 border-blue-600/20 h-16 px-4 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                />
                <Button
                  onClick={handleAnalyze}
                  className="gap-2 md:whitespace-nowrap bg-primary hover:bg-primary/90 h-16 px-8 rounded-lg font-medium text-lg"
                >
                  Analyze
                </Button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <AITeamLoading progress={progress} currentTask={currentTask} />
      )}
    </div>
  );
};
